import { memo, useMemo, useState } from "react";
import { CustomTicketV2, ThemeType } from "@markit/common.types";
import { useTheme } from "../../hooks/useTheme";
import NumberInput from "../NumberInput";
import { MAX_TICKET_PRICE } from "../../utils/makeEvent";
import { Colors } from "../../utils/colors";

type FullEventFlexiblePricingInputProps = {
  customTicket: CustomTicketV2;
  namedPrices: Map<string, number>;
  setNamedPrices: (namedPrices: Map<string, number>) => void;
  hardTheme: ThemeType;
};

// Handles the "Name Your Price" input for flexible pricing tickets
const FullEventFlexiblePricingInput = memo(
  function FullEventFlexiblePricingInputFn(
    props: FullEventFlexiblePricingInputProps
  ) {
    const { customTicket, namedPrices, setNamedPrices, hardTheme } = props;
    const { theme } = useTheme(hardTheme);
    const [displayPrice, setDisplayPrice] = useState<number | undefined>(
      customTicket.price ? customTicket.price / 100 : undefined
    ); // price in dollars

    const styles = {
      inputContainer: { padding: "8px 14px", borderRadius: 12, width: 100 },
    };

    // Shows an error if the display price is undefined and there is a min price, or if it's out of bounds
    const isError = useMemo(
      () =>
        (displayPrice === undefined && customTicket.minPrice > 0) ||
        (displayPrice !== undefined &&
          (displayPrice * 100 > MAX_TICKET_PRICE ||
            displayPrice * 100 < customTicket.minPrice)),
      [displayPrice, customTicket.minPrice]
    );

    return (
      <div className="AlignedRowSpaced" style={{ gap: 10 }}>
        <div className="ColumnNormal" style={{ gap: 4 }}>
          <span className="bodyMedium" style={theme.PrimaryText}>
            Name Your Price
          </span>
          <span
            className="smallBodySubtext"
            style={{ color: isError ? Colors.RED3 : Colors.GRAY1 }}
          >
            suggested ${customTicket.price / 100}{" "}
            {customTicket.minPrice > 0
              ? `• min $${customTicket.minPrice / 100}`
              : ""}
          </span>
        </div>
        <NumberInput
          min={customTicket.minPrice / 100}
          max={MAX_TICKET_PRICE / 100}
          prefix={"$"}
          value={displayPrice}
          onChange={(value) => {
            setDisplayPrice(value);
            const nextNamedPrices = new Map(namedPrices);
            nextNamedPrices.set(
              customTicket.id,
              value ? Math.round(value * 100) : 0
            );
            setNamedPrices(nextNamedPrices);
          }}
          containerStyles={styles.inputContainer}
          allowDecimal
          suppressErrorMessage
          hardTheme={hardTheme}
        />
      </div>
    );
  }
);

export default FullEventFlexiblePricingInput;
